"use client"

import { useState } from "react"
import { useNavigate, Link } from "react-router-dom"
import { api } from "../utils/auth"

function CreateUser() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    phone_no: "", // Added phone_no field
    role: "client", // Default role
    plan_type: "",
  })
  const [message, setMessage] = useState("")
  const [isSuccess, setIsSuccess] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const navigate = useNavigate()

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    })
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setIsLoading(true)
    try {
      const response = await api.post("/auth/signup", formData)
      setMessage(response.data.message)
      setIsSuccess(true)
      console.log("Signup successful", response.data)

      // Redirect to admin dashboard after successful user creation
      setTimeout(() => {
        navigate("/admin")
      }, 2000)
    } catch (error) {
      console.error("Signup error", error)
      setMessage(error.response?.data.message || "User creation failed. Please try again.")
      setIsSuccess(false)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="create-user-page">
      <style jsx="true">{`
        .create-user-page {
          display: flex;
          justify-content: center;
          align-items: center;
          min-height: 100vh;
          background-color: #f9fafb;
          padding: 1rem;
        }

        .create-user-container {
          background-color: #fff;
          padding: 2.5rem;
          border-radius: 0.5rem;
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
          width: 100%;
          max-width: 28rem;
        }

        .form-title {
          font-size: 1.5rem;
          font-weight: bold;
          margin-bottom: 1.5rem;
          text-align: center;
          color: #333;
        }

        .message {
          margin-bottom: 1rem;
          padding: 0.75rem;
          text-align: center;
          border-radius: 0.375rem;
          font-weight: 500;
        }

        .message-success {
          background-color: #d1fae5;
          color: #047857;
        }

        .message-error {
          background-color: #fee2e2;
          color: #b91c1c;
        }

        .form-group {
          margin-bottom: 1.25rem;
        }

        .form-label {
          display: block;
          color: #374151;
          margin-bottom: 0.5rem;
          font-weight: 500;
        }

        .form-input,
        .form-select {
          width: 100%;
          border: 1px solid #d1d5db;
          padding: 0.75rem;
          border-radius: 0.375rem;
          transition: border-color 0.15s ease-in-out;
        }

        .form-input:focus,
        .form-select:focus {
          outline: none;
          border-color: #007BFF;
          box-shadow: 0 0 0 3px rgba(0, 169, 157, 0.1);
        }

        .form-select {
          background-color: white;
        }

        .submit-button {
          width: 100%;
          background-color: #007BFF;
          color: #fff;
          padding: 0.75rem;
          border-radius: 0.375rem;
          cursor: pointer;
          font-weight: bold;
          border: none;
          transition: background-color 0.2s;
          display: flex;
          justify-content: center;
          align-items: center;
        }

        .submit-button:hover {
          background-color: #008f85;
        }

        .submit-button:disabled {
          opacity: 0.7;
          cursor: not-allowed;
        }

        .back-link {
          text-align: center;
          margin-top: 1.5rem;
          color: #4b5563;
        }

        .back-link a {
          color: #007BFF;
          text-decoration: none;
          font-weight: 500;
        }

        .back-link a:hover {
          text-decoration: underline;
        }

        .spinner {
          border: 3px solid rgba(255, 255, 255, 0.3);
          border-radius: 50%;
          border-top: 3px solid white;
          width: 1rem;
          height: 1rem;
          animation: spin 1s linear infinite;
          margin-right: 0.5rem;
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>

      <div className="create-user-container">
        <h2 className="form-title">Create New User</h2>

        {message && <div className={`message ${isSuccess ? "message-success" : "message-error"}`}>{message}</div>}

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label">Full Name</label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              className="form-input"
              placeholder="Enter full name"
              required
            />
          </div>

          <div className="form-group">
            <label className="form-label">Email Address</label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              className="form-input"
              placeholder="Enter email"
              required
            />
          </div>

          <div className="form-group">
            <label className="form-label">Password</label>
            <input
              type="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              className="form-input"
              placeholder="Set password"
              required
            />
          </div>

          {/* New Phone Number Field */}
          <div className="form-group">
            <label className="form-label">Phone Number</label>
            <input
              type="tel" // Use type="tel" for phone numbers
              name="phone_no" // Matches the state property and SQL column name
              value={formData.phone_no}
              onChange={handleChange}
              className="form-input"
              placeholder="Enter phone number (optional)"
              // You can add 'required' if you want it to be a mandatory field
            />
          </div>

          <div className="form-group">
            <label className="form-label">Role</label>
            <select name="role" value={formData.role} onChange={handleChange} className="form-select">
              <option value="client">Client</option>
              <option value="admin">Admin</option>
            </select>
          </div>

          <div className="form-group">
            <label className="form-label">Plan Type (Optional)</label>
            <select name="plan_type" value={formData.plan_type} onChange={handleChange} className="form-select">
              <option value="free">Free</option>
              <option value="trial">Trial</option>
              <option value="professional">Professional</option>
              <option value="bussiness">Bussiness</option>
            </select>
          </div>

          <button type="submit" className="submit-button" disabled={isLoading}>
            {isLoading ? (
              <>
                <div className="spinner"></div>
                Creating User...
              </>
            ) : (
              "Create User"
            )}
          </button>
        </form>

        <p className="back-link">
          <Link to="/admin">Back to Dashboard</Link>
        </p>
      </div>
    </div>
  )
}

export default CreateUser